import React, { useMemo, useState } from 'react';
import { Drawer, Select } from 'barte-design-system';
import { UI_ATTR } from './anchor';
import { Comment, PanelFilter } from './types';
import { useComments } from './useCommentsState';
import { routeLabel, timeAgo } from './util';

export function CommentsDrawer() {
  const { drawerOpen, setDrawerOpen, allComments, navigateToThread } = useComments();
  const [filter, setFilter] = useState<PanelFilter>('open');
  const [authorFilter, setAuthorFilter] = useState<string | null>(null);

  const authors = useMemo(
    () => Array.from(new Set(allComments.map((c) => c.author_name))).sort(),
    [allComments]
  );

  // Threads (raiz) que passam nos filtros, agrupadas por rota.
  const groups = useMemo(() => {
    const roots = allComments.filter((c) => c.is_root);
    const byThread = (id: string) => allComments.filter((c) => c.thread_id === id);

    const passing = roots.filter((r) => {
      if (filter === 'open' && r.resolved) return false;
      if (filter === 'resolved' && !r.resolved) return false;
      if (authorFilter) {
        const participants = byThread(r.thread_id).map((c) => c.author_name);
        if (!participants.includes(authorFilter)) return false;
      }
      return true;
    });

    const map = new Map<string, Comment[]>();
    passing.forEach((r) => {
      const arr = map.get(r.route) || [];
      arr.push(r);
      map.set(r.route, arr);
    });
    return Array.from(map.entries()).map(([route, roots2]) => ({
      route,
      roots: roots2.sort((a, b) => b.created_at.localeCompare(a.created_at)),
    }));
  }, [allComments, filter, authorFilter]);

  if (!drawerOpen) return null;

  return (
    <div {...{ [UI_ATTR]: '' }}>
      <Drawer isOpen title="Comentários" onClose={() => setDrawerOpen(false)}>
        <div className="bc-drawer-filters-row">
          <Select
            label="Status"
            size="md"
            value={filter}
            options={[
              { label: 'Abertos', value: 'open' },
              { label: 'Resolvidos', value: 'resolved' },
              { label: 'Todos', value: 'all' },
            ]}
            onChange={(v) => setFilter(v as PanelFilter)}
          />
          <Select
            label="Autor"
            size="md"
            value={authorFilter ?? '__all__'}
            options={[
              { label: 'Todos os autores', value: '__all__' },
              ...authors.map((a) => ({ label: a, value: a })),
            ]}
            onChange={(v) => setAuthorFilter(v === '__all__' ? null : v)}
          />
        </div>

        {groups.length === 0 && <p className="bc-muted">Nenhum comentário neste filtro.</p>}

        {groups.map((g) => (
          <div key={g.route} className="bc-drawer-group">
            <div className="bc-drawer-group__head">
              <span className="bc-drawer-group__route">
                {routeLabel(g.route)} — {g.roots.length}
              </span>
            </div>
            {g.roots.map((r) => (
              <button
                key={r.id}
                type="button"
                className="bc-drawer-item"
                onClick={() => navigateToThread(r.thread_id)}
              >
                <div className="bc-row" style={{ marginBottom: 4 }}>
                  <span
                    className={
                      'bc-status-dot ' +
                      (r.resolved ? 'bc-status-dot--resolved' : 'bc-status-dot--open')
                    }
                  />
                  <span className="bc-comment__author">{r.author_name}</span>
                  <span className="bc-spacer" />
                  <span className="bc-comment__time">{timeAgo(r.created_at)}</span>
                </div>
                <div className="bc-drawer-item__snippet">{r.body}</div>
              </button>
            ))}
          </div>
        ))}
      </Drawer>
    </div>
  );
}
